<template>
  <div class="app-root" :class="{ 'dark-theme': isDark, 'light-theme': !isDark }">
    <!-- Klistrad toppsektion (header + filter) som anpassar sig efter mobil & safe areas -->
    <div class="sticky-header-wrapper">
      <!-- Toppmeny i Omni-stil med kapybara-ikon och språkväljare -->
      <NewsHeader 
        :lang="lang"
        v-model:searchQuery="searchQuery"
        :is-loading="isLoading"
        :is-dark="isDark"
        :is-compact="isCompact"
        @refresh="handleRefresh(false, true)"
        @toggle-theme="toggleTheme"
        @toggle-compact="toggleCompact"
        @change-lang="handleChangeLang"
      />

      <!-- Filterrad för land (Internationellt, Tyskland, Sverige, Alla nyheter) och källor rangordnade efter antal -->
      <SourceFilters 
        :lang="lang"
        :countries="countries"
        :selected-country="selectedCountry"
        :country-counts="countryCounts"
        :sources="sources"
        :selected-source="selectedSource"
        :counts="sourceCounts"
        :total-count="countryTotalCount"
        @select-country="handleSelectCountry"
        @select-source="handleSelectSource"
      />
    </div>

    <!-- Offlineläge-indikator -->
    <div v-if="isOffline" class="offline-banner" role="status">
      <span>📡 {{ t('offlineMode') }}</span>
    </div>

    <!-- Flytande knapp för nya artiklar i Omni-stil (uppdateras utan sidomladdning) -->
    <transition name="pill-fade">
      <button 
        v-if="pendingNewArticles.length > 0"
        type="button"
        class="floating-update-pill"
        @click="applyNewArticles"
        :aria-label="t('newArticlesAvailable', { count: pendingNewArticles.length })"
      >
        <span class="pill-icon" aria-hidden="true">✨</span>
        <span class="pill-text">{{ t('newArticlesAvailable', { count: pendingNewArticles.length }) }}</span>
        <span class="pill-arrow" aria-hidden="true">↑</span>
      </button>
    </transition>

    <!-- Huvudflöde med oändlig scrollning (20 nyheter i taget) -->
    <NewsFeed 
      :lang="lang"
      :items="visibleArticles"
      :total-count="filteredArticles.length"
      :has-more="hasMore"
      :is-loading="isLoading"
      :error-message="errorMessage"
      :is-compact="isCompact"
      :has-active-filter="hasActiveFilter"
      @retry="handleRefresh(false, true)"
      @reset-filters="resetFilters"
      @load-more="handleLoadMore"
    />
  </div>
</template>

<script>
import NewsHeader from './components/NewsHeader.vue'
import SourceFilters from './components/SourceFilters.vue'
import NewsFeed from './components/NewsFeed.vue'
import { NEWS_SOURCES, COUNTRIES } from './services/rssFeeds'
import { fetchNews } from './services/newsService'
import { formatRelativeTimeLang } from './services/i18nHelper'
import { t } from './services/i18n'

export default {
  name: 'App',
  components: {
    NewsHeader,
    SourceFilters,
    NewsFeed
  },
  data() {
    return {
      lang: 'en', // Default språk: engelska
      sources: NEWS_SOURCES,
      countries: COUNTRIES,
      articles: [],
      pendingNewArticles: [],
      isOffline: typeof navigator !== 'undefined' ? !navigator.onLine : false,
      isLoading: false,
      errorMessage: '',
      selectedCountry: 'INT', // Internationellt som första alternativ & default
      selectedSource: 'all',
      searchQuery: '',
      isDark: false,
      isCompact: false,
      pageSize: 20,
      displayedCount: 20,
      refreshTimer: null,
      pollTimer: null,
      timeTicker: null
    }
  },
  computed: {
    hasActiveFilter() {
      return (
        this.selectedCountry !== 'INT' ||
        this.selectedSource !== 'all' ||
        this.searchQuery.trim().length > 0
      )
    },
    countryCounts() {
      const counts = { all: this.articles.length, INT: 0, DE: 0, SE: 0 }
      for (const item of this.articles) {
        if (item.country === 'SE') {
          counts.SE++
        } else if (item.country === 'DE') {
          counts.DE++
        } else {
          counts.INT++
        }
      }
      return counts
    },
    sourceCounts() {
      const counts = {}
      for (const source of this.sources) {
        counts[source.id] = 0
      }
      // Räkna artiklar inom valt land
      const baseArticles = this.selectedCountry === 'all' 
        ? this.articles 
        : this.articles.filter(item => item.country === this.selectedCountry)

      for (const item of baseArticles) {
        if (counts[item.sourceId] !== undefined) {
          counts[item.sourceId]++
        }
      }
      return counts
    },
    countryTotalCount() {
      let base = this.selectedCountry === 'all'
        ? this.articles
        : this.articles.filter(item => item.country === this.selectedCountry)
      const query = this.searchQuery.trim().toLowerCase()
      if (query) {
        base = base.filter(item => {
          const title = item.title ? item.title.toLowerCase() : ''
          const source = item.sourceName ? item.sourceName.toLowerCase() : ''
          return title.includes(query) || source.includes(query)
        })
      }
      return base.length
    },
    filteredArticles() {
      let result = this.articles

      // 1. Filtrera på land
      if (this.selectedCountry !== 'all') {
        result = result.filter(item => item.country === this.selectedCountry)
      }

      // 2. Filtrera på källa
      if (this.selectedSource !== 'all') {
        result = result.filter(item => item.sourceId === this.selectedSource)
      }

      // 3. Filtrera på sökord
      const query = this.searchQuery.trim().toLowerCase()
      if (query) {
        result = result.filter(item => {
          const title = item.title ? item.title.toLowerCase() : ''
          const source = item.sourceName ? item.sourceName.toLowerCase() : ''
          return title.includes(query) || source.includes(query)
        })
      }

      return result
    },
    visibleArticles() {
      return this.filteredArticles.slice(0, this.displayedCount)
    },
    hasMore() {
      return this.visibleArticles.length < this.filteredArticles.length
    }
  },
  watch: {
    searchQuery() {
      this.displayedCount = this.pageSize
    },
    lang() {
      this.updateMeta()
    },
    selectedCountry() {
      this.updateMeta()
    }
  },
  created() {
    // Läs sparad språkinställning (default 'en')
    const savedLang = localStorage.getItem('wokenews_lang')
    if (savedLang === 'sv' || savedLang === 'en' || savedLang === 'de') {
      this.lang = savedLang
    } else {
      this.lang = 'en'
    }
    if (typeof document !== 'undefined') {
      document.documentElement.lang = this.lang
    }

    // Läs sparade inställningar för mörkt tema
    const savedTheme = localStorage.getItem('wokenews_theme')
    if (savedTheme) {
      this.isDark = savedTheme === 'dark'
    } else {
      const prefersDark = window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches
      this.isDark = prefersDark
    }

    // Läs sparad vy-inställning
    const savedCompact = localStorage.getItem('wokenews_compact')
    if (savedCompact) {
      this.isCompact = savedCompact === 'true'
    }
  },
  mounted() {
    this.updateMeta()
    this.handleRefresh(false, false)

    if (typeof window !== 'undefined') {
      window.addEventListener('online', this.updateOnlineStatus)
      window.addEventListener('offline', this.updateOnlineStatus)
    }

    // Automatisk bakgrundspollning efter nya nyheter var 60:e sekund
    this.pollTimer = setInterval(() => {
      this.checkNewArticles()
    }, 60 * 1000)

    // Uppdatera tidsstämplar (t.ex. "5 min ago") varje minut
    this.timeTicker = setInterval(() => {
      this.refreshTimeAgos()
    }, 60 * 1000)
  },
  beforeUnmount() {
    if (this.refreshTimer) clearInterval(this.refreshTimer)
    if (this.pollTimer) clearInterval(this.pollTimer)
    if (this.timeTicker) clearInterval(this.timeTicker)
    if (typeof window !== 'undefined') {
      window.removeEventListener('online', this.updateOnlineStatus)
      window.removeEventListener('offline', this.updateOnlineStatus)
    }
  },
  methods: {
    t(key, params) {
      return t(this.lang, key, params)
    },
    updateOnlineStatus() {
      if (typeof navigator !== 'undefined') {
        this.isOffline = !navigator.onLine
      }
    },
    async checkNewArticles() {
      // Hoppa över om redan laddar eller om enheten är offline
      if (this.isLoading || this.isOffline) return

      try {
        const incoming = await fetchNews(false, this.lang)
        if (!incoming || incoming.length === 0) return

        // Jämför ID och länk mot nuvarande artiklar
        const existingLinks = new Set(this.articles.map(a => a.link))
        const existingIds = new Set(this.articles.map(a => a.id))
        const pendingLinks = new Set(this.pendingNewArticles.map(a => a.link))

        const brandNew = incoming.filter(item => 
          !existingLinks.has(item.link) && 
          !existingIds.has(item.id) &&
          !pendingLinks.has(item.link)
        )

        if (brandNew.length > 0) {
          if (this.articles.length === 0) {
            this.articles = incoming
          } else {
            // Lägg till i listan över väntande nya nyheter
            this.pendingNewArticles = [...brandNew, ...this.pendingNewArticles]
          }
        }
      } catch {
        // Tyst vid bakgrundskontroll
      }
    },
    applyNewArticles() {
      if (this.pendingNewArticles.length === 0) return
      // Slå ihop de nya artiklarna längst upp i flödet
      const toPrepend = this.pendingNewArticles
      this.articles = [...toPrepend, ...this.articles]
      this.pendingNewArticles = []

      // Rulla mjukt upp till toppen
      if (typeof window !== 'undefined') {
        window.scrollTo({ top: 0, behavior: 'smooth' })
      }
    },
    updateMeta() {
      if (typeof document === 'undefined') return
      let title = 'Woke News — Curated News Feed'
      let desc = 'Minimalist news feed aggregating headlines from NYT, The Guardian, SVT, Der Spiegel, DW, Vox, and NPR.'

      if (this.lang === 'sv') {
        title = 'Woke News — Kurerat nyhetsflöde'
        desc = 'Ett minimalistiskt och snabbt nyhetsflöde med rubriker från The New York Times, The Guardian, SVT, Der Spiegel m.fl.'
      } else if (this.lang === 'de') {
        title = 'Woke News — Kuratierter Feed'
        desc = 'Ein minimalistischer Nachrichten-Feed mit Schlagzeilen von The New York Times, The Guardian, SVT, Der Spiegel, DW u.a.'
      }

      if (this.selectedCountry === 'SE') {
        title = this.lang === 'sv' ? 'Woke News — Svenska nyheter' : (this.lang === 'de' ? 'Woke News — Schweden' : 'Woke News — Sweden News')
      } else if (this.selectedCountry === 'DE') {
        title = this.lang === 'sv' ? 'Woke News — Tyskland (på engelska)' : (this.lang === 'de' ? 'Woke News — Deutschland (EN)' : 'Woke News — Germany (EN)')
      } else if (this.selectedCountry === 'INT') {
        title = this.lang === 'sv' ? 'Woke News — Internationella nyheter' : (this.lang === 'de' ? 'Woke News — Internationale Nachrichten' : 'Woke News — International News')
      }

      document.title = title
      const metaDesc = document.querySelector('meta[name="description"]')
      if (metaDesc) metaDesc.setAttribute('content', desc)
    },
    async handleRefresh(background = false, forceRefresh = false) {
      if (!background) {
        this.isLoading = true
      }
      this.errorMessage = ''

      try {
        const data = await fetchNews(forceRefresh, this.lang)
        if (data && data.length > 0) {
          this.articles = data
        } else if (this.articles.length === 0) {
          this.errorMessage = this.lang === 'sv' 
            ? 'Inga nyheter kunde hämtas just nu.' 
            : (this.lang === 'de' ? 'Nachrichten konnten derzeit nicht geladen werden.' : 'No news could be retrieved right now.')
        }
      } catch (err) {
        if (this.articles.length === 0) {
          this.errorMessage = this.lang === 'sv'
            ? 'Ett nätverksfel uppstod vid inläsning av nyheterna.'
            : (this.lang === 'de' ? 'Ein Netzwerkfehler ist beim Laden der Nachrichten aufgetreten.' : 'A network error occurred while loading news.')
        }
      } finally {
        this.isLoading = false
      }
    },
    refreshTimeAgos() {
      if (!this.articles.length) return
      this.articles = this.articles.map(item => ({
        ...item,
        timeAgo: formatRelativeTimeLang(item.pubDate, this.lang)
      }))
    },
    handleChangeLang(newLang) {
      this.lang = newLang
      localStorage.setItem('wokenews_lang', this.lang)
      if (typeof document !== 'undefined') {
        document.documentElement.lang = this.lang
      }
      this.refreshTimeAgos()
    },
    handleSelectCountry(countryId) {
      this.selectedCountry = countryId
      this.selectedSource = 'all'
      this.displayedCount = this.pageSize
    },
    handleSelectSource(sourceId) {
      this.selectedSource = sourceId
      this.displayedCount = this.pageSize
    },
    handleLoadMore() {
      if (this.hasMore) {
        this.displayedCount = Math.min(
          this.displayedCount + this.pageSize,
          this.filteredArticles.length
        )
      }
    },
    resetFilters() {
      this.selectedCountry = 'INT'
      this.selectedSource = 'all'
      this.searchQuery = ''
      this.displayedCount = this.pageSize
    },
    toggleTheme() {
      this.isDark = !this.isDark
      localStorage.setItem('wokenews_theme', this.isDark ? 'dark' : 'light')
    },
    toggleCompact() {
      this.isCompact = !this.isCompact
      localStorage.setItem('wokenews_compact', this.isCompact ? 'true' : 'false')
    }
  }
}
</script>

<style>
/* CSS Reset och variabler för Omni-stilad estetik */
*, *::before, *::after {
  box-sizing: border-box;
}

:root {
  /* Ljust tema */
  --bg-primary: #f6f7f9;
  --header-bg: rgba(255, 255, 255, 0.94);
  --card-bg: #ffffff;
  --card-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
  --group-header-bg: #f9fafb;
  --border-color: #e5e7eb;
  --item-hover-bg: #f8fafc;
  --text-primary: #111827;
  --text-muted: #6b7280;
  --accent-color: #111827;
  --accent-hover: #0369a1;
  --pill-bg: #f3f4f6;
  --pill-text: #374151;
  --pill-border: #e5e7eb;
  --pill-hover-bg: #e5e7eb;
  --pill-hover-border: #d1d5db;
  --pill-active-bg: #111827;
  --pill-active-text: #ffffff;
  --input-bg: #f3f4f6;
  --btn-bg: #f3f4f6;
  --btn-hover-bg: #e5e7eb;
  --badge-count-bg: #e5e7eb;
  --skeleton-bg: #e5e7eb;
}

.dark-theme {
  /* Mörkt tema (Omni dark mode) */
  --bg-primary: #0d0f12;
  --header-bg: rgba(18, 20, 24, 0.95);
  --card-bg: #14171d;
  --card-shadow: 0 2px 8px rgba(0, 0, 0, 0.4);
  --group-header-bg: #1a1e26;
  --border-color: #272b35;
  --item-hover-bg: #1a1f29;
  --text-primary: #f3f4f6;
  --text-muted: #94a3b8;
  --accent-color: #38bdf8;
  --accent-hover: #7dd3fc;
  --pill-bg: #1c212c;
  --pill-text: #cbd5e1;
  --pill-border: #2c3240;
  --pill-hover-bg: #272f3f;
  --pill-hover-border: #3b4457;
  --pill-active-bg: #38bdf8;
  --pill-active-text: #0b1120;
  --input-bg: #1c212c;
  --btn-bg: #1c212c;
  --btn-hover-bg: #272f3f;
  --badge-count-bg: #262c3a;
  --skeleton-bg: #222733;
}

body {
  margin: 0;
  padding: 0;
  background-color: var(--bg-primary);
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
  color: var(--text-primary);
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
}

.app-root {
  min-height: 100vh;
  background-color: var(--bg-primary);
  color: var(--text-primary);
  transition: background-color 0.25s ease, color 0.25s ease;
  padding-bottom: env(safe-area-inset-bottom, 0px);
}

/* Klistrad topp med safe area insets och blur */
.sticky-header-wrapper {
  position: sticky;
  top: 0;
  z-index: 100;
  background-color: var(--header-bg);
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  padding-top: env(safe-area-inset-top, 0px);
  transition: background-color 0.25s ease;
}

/* Offline-banner */
.offline-banner {
  background-color: #f59e0b;
  color: #1c1917;
  font-size: 0.8rem;
  font-weight: 700;
  text-align: center;
  padding: 6px 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

/* Flytande Omni-knapp för nya artiklar */
.floating-update-pill {
  position: fixed;
  top: calc(125px + env(safe-area-inset-top, 0px));
  left: 50%;
  transform: translateX(-50%);
  z-index: 95;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background-color: var(--accent-color);
  color: var(--card-bg);
  border: 1px solid var(--border-color);
  border-radius: 24px;
  padding: 8px 18px;
  font-size: 0.84rem;
  font-weight: 700;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.22);
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;
  transition: transform 0.18s ease, box-shadow 0.18s ease, background-color 0.2s ease;
}

.floating-update-pill:hover {
  transform: translateX(-50%) translateY(-2px);
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.3);
}

.floating-update-pill:active {
  transform: translateX(-50%) translateY(0);
}

.pill-icon {
  font-size: 0.95rem;
}

.pill-arrow {
  font-size: 0.95rem;
  font-weight: 900;
  transition: transform 0.2s ease;
}

.floating-update-pill:hover .pill-arrow {
  transform: translateY(-2px);
}

.pill-fade-enter-active,
.pill-fade-leave-active {
  transition: all 0.25s cubic-bezier(0.16, 1, 0.3, 1);
}

.pill-fade-enter-from,
.pill-fade-leave-to {
  opacity: 0;
  transform: translateX(-50%) translateY(-12px);
}
</style>
